import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import WalletGearModal, { PageGearButton } from './WalletGearModal';
import OpenPositionsSection from './OpenPositionsSection';
import MultiAssetTradePanel, { useTradePanelScroll } from './MultiAssetTradePanel';
import LiveOrderBook from './LiveOrderBook';
import CommissionOwedPanel from './CommissionOwedPanel';
import SignalQueueSection from './SignalQueueSection';
import ManualSignalAccessPanel from './ManualSignalAccessPanel';
import HlFundUsdcButton from './HlFundUsdcButton';
import HlFundUsdcModal from './HlFundUsdcModal';
import OrderTransactionsSection from './OrderTransactionsSection';
import HlMarketSearch from './HlMarketSearch';
import { fmtUsd, fmtDollar } from '../utils/fmtPrice';
import { formatSideLabel, positionKey, normalizeTradeAction } from '../utils/tradeActionUi';
import { displayMarketName, quoteForMarket } from '../utils/hlMarketDisplay';
import { formatHlSlippageLabel } from '../utils/hlSlippage';
import { submitManualOrderWithBrowserWallet } from '../utils/hlOrderSign';
import './ManualTrades.css';
import './CommissionOwedPanel.css';
import './HlMarketSearch.css';
import './WalletGearModal.css';
import './OpenPositionsSection.css';
import './MultiAssetTradePanel.css';
import './LiveOrderBook.css';
import './OrderTransactionsSection.css';
import './ManualSignalAccessPanel.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const defaultForm = {
  symbol: 'GOLD',
  hlDex: 'xyz',
  hlCoin: null,
  leverage: 1,
  hlMaxLeverage: null,
  side: 'long',
  action: 'open',
  sizeUsd: 5000,
  stopPrice: '',
  targetPrice: '',
  reason: '',
};

const MANUAL_SIGNAL_LEVERAGE = 2;

const MAP_SIGNAL_SOURCES = new Set(['algorithm', 'chat', 'admin', 'map']);

function pickLatestMapSignal(signals) {
  if (!Array.isArray(signals) || !signals.length) return null;
  return (
    signals.find((s) => MAP_SIGNAL_SOURCES.has(String(s.source || '').toLowerCase())) || signals[0]
  );
}

function fmtCommissionLine(quote) {
  if (!quote?.commissionEnabled) return 'None (commission disabled)';
  if (quote.commissionSkipped || !(Number(quote.commissionUsd) > 0)) {
    if (quote.commissionSkipReason === 'zero_bps') return 'None (0 bps configured)';
    if (quote.commissionSkipReason === 'below_minimum') return 'None (below minimum)';
    return 'None';
  }
  const bps = quote.commissionBps != null ? ` · ${quote.commissionBps} bps` : '';
  return `${fmtUsd(quote.commissionUsd)}${bps}`;
}

export default function ManualTrades() {
  const location = useLocation();
  const { panelRef, scrollToPanel } = useTradePanelScroll();
  const [symbols, setSymbols] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [closeQuickBusy, setCloseQuickBusy] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [fundModalOpen, setFundModalOpen] = useState(false);
  const [positionsRefresh, setPositionsRefresh] = useState(0);
  const [defaultDeployUsd, setDefaultDeployUsd] = useState(5000);
  const [tradeConfig, setTradeConfig] = useState(null);
  const [maStatus, setMaStatus] = useState(null);
  const [prefillSignalId, setPrefillSignalId] = useState(null);
  const [pendingQuote, setPendingQuote] = useState(null);
  const [canViewMapSignals, setCanViewMapSignals] = useState(false);
  const appliedNavState = useRef(false);
  const lastAppliedSignalIdRef = useRef(null);
  const formOverrideRef = useRef(null); // 'position' | 'market' — pause MAP autofill until cleared

  const setStatus = (text, isError = false) => {
    if (isError) setError(text);
    else setMessage(text);
    if (isError) setMessage(null);
    else setError(null);
  };

  useEffect(() => {
    axios
      .get(`${API_BASE}/multi-asset/config`)
      .then((r) => {
        const list = r.data.allowedSymbols?.length
          ? r.data.allowedSymbols
          : r.data.knownSymbols || [];
        setSymbols(list);
        setTradeConfig(r.data);
        if (r.data.defaultDeployUsd) setDefaultDeployUsd(r.data.defaultDeployUsd);
        if (list.length && !list.includes(form.symbol)) {
          setForm((f) => ({ ...f, symbol: list[0] }));
        }
      })
      .catch(() => setSymbols(['GOLD', 'AVGO', 'COPPER', 'MRVL', 'XYZ100', 'USDJPY', 'NVDA']));
  }, []);

  useEffect(() => {
    axios
      .get(`${API_BASE}/multi-asset/status`, { headers: authHeaders() })
      .then(({ data }) => setMaStatus(data))
      .catch(() => setMaStatus(null));
  }, [positionsRefresh]);

  useEffect(() => {
    if (!pendingQuote) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) setPendingQuote(null);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [pendingQuote, submitting]);

  useEffect(() => {
    const nav = location.state?.position;
    if (!nav || appliedNavState.current) return;
    appliedNavState.current = true;
    applyPositionToForm(nav);
    setSelectedPosition(nav);
    setTimeout(scrollToPanel, 100);
  }, [location.state]);

  const applyPositionToForm = (p) => {
    formOverrideRef.current = 'position';
    lastAppliedSignalIdRef.current = null;
    setPrefillSignalId(null);
    const margin = p.marginUsd ?? p.sizeUsd ?? 5000;
    setForm({
      ...defaultForm,
      symbol: p.symbol,
      side: p.closeSide,
      action: 'close',
      sizeUsd: margin,
      leverage: p.leverage || 1,
      reason: `Close ${p.side} ${p.symbol}`,
    });
  };

  const applySignalToForm = useCallback(
    (s, { scroll = false } = {}) => {
      if (!s?.symbol) return;
      const raw = s.traderUsdc ?? s.size_usd;
      const size =
        raw != null && Number.isFinite(Number(raw))
          ? Math.max(5, Math.round(Number(raw)))
          : defaultDeployUsd;
      const lev = Math.max(1, Number(s.leverage) || MANUAL_SIGNAL_LEVERAGE);
      const action = normalizeTradeAction(s.action || 'open');
      const stop = s.stopPrice ?? s.stop_price;
      const target = s.targetPrice ?? s.target_price;

      setSelectedPosition(null);
      setSymbols((prev) => (prev.includes(s.symbol) ? prev : [...prev, s.symbol]));
      setPrefillSignalId(s.id);
      setForm((f) => ({
        ...f,
        symbol: s.symbol,
        hlDex: s.hlDex || f.hlDex || 'xyz',
        hlCoin: s.hlCoin || null,
        side: String(s.side || '').toLowerCase() === 'short' ? 'short' : 'long',
        action,
        sizeUsd: size,
        leverage: lev,
        stopPrice: stop != null && stop !== '' ? String(stop) : '',
        targetPrice: target != null && target !== '' ? String(target) : '',
        reason: s.reason ? `MAP signal #${s.id} — ${s.reason}` : `MAP signal #${s.id}`,
      }));
      if (scroll) scrollToPanel();
    },
    [defaultDeployUsd, scrollToPanel]
  );

  const handleTradeFromSignal = useCallback(
    (signal) => {
      if (!signal?.id) return;
      if (signal.alreadyInPosition) {
        setError(
          `You already have an open ${signal.side} ${signal.symbol} position. Close or reduce it first, or use Add to increase size.`
        );
        setMessage(null);
        return;
      }
      formOverrideRef.current = 'signal';
      lastAppliedSignalIdRef.current = signal.id;
      applySignalToForm(signal, { scroll: true });
      const pct =
        signal.mapWeightPct != null
          ? `${Number(signal.mapWeightPct).toFixed(2)}% of Omni MAP book`
          : 'MAP book %';
      const avail =
        signal.traderDeployableUsd != null &&
        signal.traderUsdc != null &&
        Number(signal.traderDeployableUsd) + 0.5 < Number(signal.traderUsdc)
          ? ` · ${fmtUsd(signal.traderDeployableUsd)} deployable now (thesis ${fmtUsd(signal.traderUsdc)})`
          : '';
      setStatus(
        `Trade panel filled from signal #${signal.id}: ${signal.side} ${signal.symbol}, margin ${fmtUsd(signal.traderUsdc ?? signal.size_usd)} (${pct})${avail}. Stop/target prefilled when MAP provides them — review before submit.`
      );
    },
    [applySignalToForm]
  );

  const handleSignalRefresh = useCallback(
    (signal) => {
      if (!signal?.id || selectedPosition || !prefillSignalId) return;
      if (signal.id !== prefillSignalId) return;
      if (formOverrideRef.current === 'position' || formOverrideRef.current === 'market') return;
      const thesis =
        signal.traderUsdc != null && Number.isFinite(Number(signal.traderUsdc))
          ? Math.max(5, Math.round(Number(signal.traderUsdc)))
          : null;
      const stop = signal.stopPrice ?? signal.stop_price;
      const target = signal.targetPrice ?? signal.target_price;
      setForm((f) => ({
        ...f,
        stopPrice: stop != null && stop !== '' ? String(stop) : f.stopPrice,
        targetPrice: target != null && target !== '' ? String(target) : f.targetPrice,
        sizeUsd: thesis != null ? Math.max(f.sizeUsd, thesis) : f.sizeUsd,
      }));
    },
    [selectedPosition, prefillSignalId]
  );

  const pickMapSignal = useCallback((list) => pickLatestMapSignal(list), []);

  const handleSelectPosition = (p) => {
    setSelectedPosition(p);
    applyPositionToForm(p);
    scrollToPanel();
  };

  const handleTradeLot = (p) => {
    setSelectedPosition(p);
    applyPositionToForm(p);
    scrollToPanel();
    const margin = p.marginUsd ?? p.sizeUsd ?? 0;
    const lev = p.leverage || 1;
    const total = p.notionalUsd != null ? p.notionalUsd : margin * lev;
    setStatus(
      p.side === 'long'
        ? `Partial sell — reduce ${p.symbol} long. Margin ${fmtUsd(margin)} · ${lev}x (≈ ${fmtUsd(total)}). Adjust margin below for a partial close.`
        : `Partial buy — reduce ${p.symbol} short. Margin ${fmtUsd(margin)} · ${lev}x (≈ ${fmtUsd(total)}). Adjust margin below for a partial close.`
    );
  };

  const handleCloseLot = async (p) => {
    setSelectedPosition(p);
    applyPositionToForm(p);
    const margin = p.marginUsd ?? p.sizeUsd ?? 5000;
    let hlDex = form.hlDex || 'xyz';
    let hlCoin = form.hlCoin;
    if (p.symbol !== form.symbol || !hlCoin) {
      try {
        const { data: meta } = await axios.get(`${API_BASE}/multi-asset/market-meta/${p.symbol}`, {
          headers: authHeaders(),
          params: { dex: hlDex },
        });
        hlDex = meta.dex || hlDex;
        hlCoin = meta.coin || hlCoin;
      } catch {
        /* use defaults */
      }
    }
    try {
      await queueTrade({
        symbol: p.symbol,
        hlDex,
        hlCoin,
        side: p.closeSide,
        action: 'close',
        sizeUsd: Number(margin),
        leverage: p.leverage || 1,
        tradingMode: 'manual',
        source: 'manual',
        reason: `Close ${p.side} ${p.symbol}`,
        payload: {
          closeContractsExact: Number(p.sizeContracts) || undefined,
        },
      });
    } catch {
      /* error set in queueTrade */
    }
  };

  useEffect(() => {
    const sym = form.symbol;
    const dex = form.hlDex || 'xyz';
    if (!sym) return;
    axios
      .get(`${API_BASE}/multi-asset/market-meta/${sym}`, {
        headers: authHeaders(),
        params: { dex },
      })
      .then(({ data }) => {
        setForm((f) => ({
          ...f,
          hlMaxLeverage: data.maxLeverage ?? null,
          hlDex: data.dex || f.hlDex,
          hlCoin: data.coin || f.hlCoin,
          leverage:
            f.leverage && data.maxLeverage && f.leverage <= data.maxLeverage
              ? f.leverage
              : Math.min(f.leverage || MANUAL_SIGNAL_LEVERAGE, data.maxLeverage || MANUAL_SIGNAL_LEVERAGE),
        }));
      })
      .catch(() => {});
  }, [form.symbol, form.hlDex]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await queueTrade({
        symbol: form.symbol,
        hlDex: form.hlDex,
        hlCoin: form.hlCoin,
        side: form.side,
        action: form.action,
        sizeUsd: Number(form.sizeUsd),
        stopPrice: form.stopPrice ? Number(form.stopPrice) : undefined,
        targetPrice: form.targetPrice ? Number(form.targetPrice) : undefined,
        leverage: Number(form.leverage) || 1,
        tradingMode: 'manual',
        source: 'manual',
        reason: form.reason || 'Manual trade from dashboard',
      });
    } catch {
      /* error set in queueTrade */
    }
  };

  const queueTrade = async (payload) => {
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await axios.post(`${API_BASE}/multi-asset/signals`, payload, {
        headers: authHeaders(),
      });

      if (data.requiresQuoteConfirm) {
        setPendingQuote(data);
        return data;
      }

      if (data.requiresBrowserSignature) {
        setMessage(`Signal #${data.signalId} prepared — confirm in your wallet…`);
        const submitted = await submitManualOrderWithBrowserWallet({
          walletAddress: data.walletAddress,
          signalId: data.signalId,
          apiBase: API_BASE,
          authHeaders,
        });
        setMessage(
          submitted.message ||
            `Live order submitted for ${payload.symbol} (${formatSideLabel(payload.side, payload.action)} ${payload.action}).`
        );
      } else {
        setMessage(
          `Signal #${data.signalId} queued for ${payload.symbol} (${formatSideLabel(payload.side, payload.action)} · ${payload.action})${
            data.paperMode ? ' — paper fill' : ''
          }.`
        );
      }
      setPositionsRefresh((k) => k + 1);
      if (payload.action === 'close') setSelectedPosition(null);
      return data;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPaperQuote = async () => {
    if (!pendingQuote?.signalId) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/signals/${pendingQuote.signalId}/confirm`,
        {},
        { headers: authHeaders() }
      );
      setMessage(data.message || `Paper fill for ${pendingQuote.symbol}.`);
      setPendingQuote(null);
      setPositionsRefresh((k) => k + 1);
      if (String(pendingQuote.actionType || form.action).toLowerCase() === 'close') {
        setSelectedPosition(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const paperMode = tradeConfig?.paperMode !== false;
  const linkedWalletAddress =
    maStatus?.wallets?.find((w) => w.walletAddress)?.walletAddress ||
    maStatus?.wallet?.walletAddress ||
    null;
  const marginQuote = quoteForMarket(form);
  const quoteMarketLabel = pendingQuote
    ? displayMarketName({
        symbol: pendingQuote.symbol,
        dex: pendingQuote.hlDex || form.hlDex,
        displayName: form.symbol === pendingQuote.symbol ? null : pendingQuote.symbol,
      })
    : '';

  const handleMarketSelect = (market, opts = {}) => {
    if (!market?.symbol) return;
    formOverrideRef.current = 'market';
    lastAppliedSignalIdRef.current = null;
    setSelectedPosition(null);
    setPrefillSignalId(null);
    const label = market.displayName || market.symbol;
    setSymbols((prev) => (prev.includes(market.symbol) ? prev : [...prev, market.symbol]));
    setForm({
      ...defaultForm,
      symbol: market.symbol,
      hlDex: market.dex || 'xyz',
      hlCoin: market.coin || null,
      hlMaxLeverage: market.maxLeverage ?? null,
      leverage: Math.min(1, market.maxLeverage || 1) || 1,
      side: 'long',
      action: 'open',
      sizeUsd: defaultDeployUsd,
      reason: `Trade ${label} (${market.dex || 'xyz'}) from HL market search`,
    });
    if (!opts.scrollOnly) scrollToPanel();
  };

  const handleQuickTrade = async (trade) => {
    try {
      await queueTrade({
        symbol: trade.symbol,
        hlDex: trade.hlDex,
        hlCoin: trade.hlCoin,
        side: trade.side,
        action: trade.action || 'open',
        sizeUsd: trade.sizeUsd,
        leverage: trade.leverage || form.leverage || 1,
        tradingMode: 'manual',
        source: 'manual',
        reason: trade.reason || 'Manual trade from HL market search',
      });
      setForm((f) => ({
        ...f,
        symbol: trade.symbol,
        hlDex: trade.hlDex || f.hlDex,
        hlCoin: trade.hlCoin || f.hlCoin,
        leverage: trade.leverage || f.leverage || 1,
        side: trade.side,
        action: trade.action || 'open',
        sizeUsd: trade.sizeUsd,
      }));
    } catch {
      /* error set in queueTrade */
    }
  };

  const closePositionQuick = async (p) => {
    const margin = p.marginUsd ?? p.sizeUsd ?? 5000;
    const lev = p.leverage || 1;
    if (
      !window.confirm(
        `Close entire ${p.side} ${p.symbol}? (Margin ${fmtUsd(margin)} · ${lev}x)`
      )
    ) {
      return;
    }
    setCloseQuickBusy(true);
    try {
      await handleCloseLot(p);
    } finally {
      setCloseQuickBusy(false);
    }
  };

  return (
    <div className="manual-trades-page">
      <header className="page-header-with-gear">
        <div className="page-header-main">
          <p>
            <Link to="/multi-asset">← Multi-Asset Trading</Link>
          </p>
          <h1>Manual Trades</h1>
          <p className="subtitle">
            MAP signals at the top, then queue manual trades with the live book. Open positions below are from this
            page only (not oBot). Wallet link via the icon above; cash reserve in{' '}
            <Link to="/multi-asset">Multi-Asset → Settings</Link>.
          </p>
        </div>
        <div className="page-header-actions">
          {!paperMode && linkedWalletAddress && (
            <HlFundUsdcButton onClick={() => setFundModalOpen(true)} />
          )}
          <PageGearButton
            onClick={() => setWalletModalOpen(true)}
            label="Manual wallet link"
            icon="wallet"
          />
        </div>
      </header>

      <WalletGearModal
        open={walletModalOpen}
        onClose={() => setWalletModalOpen(false)}
        title="Manual wallet link"
        view="manual"
      />

      <HlFundUsdcModal
        open={fundModalOpen}
        onClose={() => setFundModalOpen(false)}
        walletAddress={linkedWalletAddress}
        onFunded={() => {
          setPositionsRefresh((n) => n + 1);
          setMessage('USDC credited on Hyperliquid — retry builder approval or live trading.');
        }}
      />

      {error && <div className="manual-error">{error}</div>}
      {message && <div className="manual-success">{message}</div>}

      {tradeConfig && (
        <div
          className={`manual-mode-banner ${
            tradeConfig.paperMode !== false ? 'paper' : 'live'
          }`}
          role="status"
          aria-live="polite"
        >
          <span className="manual-mode-label">Trading mode</span>
          <span
            className={`manual-mode-pill ${
              tradeConfig.paperMode !== false ? 'paper' : 'live'
            }`}
          >
            {tradeConfig.paperMode !== false ? 'PAPER' : 'LIVE'}
          </span>
          <span className="manual-mode-detail">
            {tradeConfig.paperMode === false
              ? `Real orders on Hyperliquid — suggested margin uses your HL wallet (${
                  maStatus?.manualSizingBalanceUsd != null
                    ? fmtUsd(maStatus.manualSizingBalanceUsd)
                    : 'link wallet in gear icon'
                } deployable after reserve).`
              : `Simulated fills — suggested margin uses your paper test balance (${
                  maStatus?.manualSizingBalanceUsd != null
                    ? fmtUsd(maStatus.manualSizingBalanceUsd)
                    : fmtUsd(maStatus?.paperTestBalanceUsd ?? 10000)
                } after reserve). Change in Multi-Asset → Settings (gear).`}
          </span>
        </div>
      )}

      <CommissionOwedPanel refreshKey={positionsRefresh} onPaid={() => setPositionsRefresh((k) => k + 1)} />

      <ManualSignalAccessPanel
        refreshKey={positionsRefresh}
        onStatusChange={(s) => setCanViewMapSignals(!!(s?.isAdmin || s?.hasAccess))}
        onAccessGranted={() => setCanViewMapSignals(true)}
      />

      <SignalQueueSection
        refreshKey={positionsRefresh}
        variant="manual"
        feed="manual"
        enabled={canViewMapSignals}
        title="Signals"
        limit={50}
        visibleRows={5}
        onTradeSignal={handleTradeFromSignal}
        onLatestSignal={handleSignalRefresh}
        latestSignalFilter={pickMapSignal}
        hint="MAP recommendations refresh every 30s. Symbol shows live HL mid. Suggested margin = same MAP book % applied to your paper test balance or live HL wallet. Range/stop is the MAP zone — Trade copies margin, side, leverage, and stop into the form below."
      />

      <MultiAssetTradePanel
        panelRef={panelRef}
        form={form}
        setForm={setForm}
        symbols={symbols.length ? symbols : [form.symbol]}
        submitting={submitting}
        onSubmit={submit}
        selectedPosition={selectedPosition}
        onClosePositionQuick={closePositionQuick}
        closeQuickBusy={closeQuickBusy}
        tradingMode="manual"
        leverageCap={form.hlMaxLeverage || 10}
        hlMaxLeverage={form.hlMaxLeverage}
        embedOrderBook={false}
        prefillSignalId={prefillSignalId}
        introText="Waiting for MAP signal…"
        paperMode={paperMode}
      />

      <section className="manual-live-book-section">
        <LiveOrderBook
          symbol={form.symbol}
          hlDex={form.hlDex || 'xyz'}
          hlCoin={form.hlCoin}
          titlePrefix="Live book"
        />
      </section>

      <OpenPositionsSection
        selectedPositionKey={selectedPosition ? positionKey(selectedPosition) : null}
        onSelectPosition={handleSelectPosition}
        onTradeLot={handleTradeLot}
        onCloseLot={handleCloseLot}
        onStatusMessage={setStatus}
        onPositionsChanged={() => setPositionsRefresh((k) => k + 1)}
        refreshKey={positionsRefresh}
        scope="manual"
      />

      <OrderTransactionsSection
        orders={maStatus?.recentOrders || []}
        hint="Recent manual and multi-asset order activity for your account."
      />

      <HlMarketSearch
        defaultSizeUsd={defaultDeployUsd}
        busy={submitting}
        onSelectSymbol={handleMarketSelect}
        onQuickTrade={handleQuickTrade}
        minDeployUsd={5}
      />

      <p className="manual-foot">
        For automated Omni MAP trades, use <Link to="/multi-asset/obot">oBot Trading</Link> — save your wallet in{' '}
        <Link to="/multi-asset">Settings</Link>, then Start oBot from the gear icon.
      </p>

      {pendingQuote && (
        <div
          className="manual-quote-overlay"
          role="presentation"
          onClick={() => !submitting && setPendingQuote(null)}
        >
          <div
            className="manual-quote-dialog"
            role="dialog"
            aria-labelledby="manual-quote-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="manual-quote-title">Confirm paper trade</h2>
            <p className="manual-quote-sub">
              Review the simulated fill below. No wallet or Hyperliquid deposit is required — commission is
              recorded in paper mode only.
            </p>
            <dl className="manual-quote-grid">
              <dt>Market</dt>
              <dd>
                {quoteMarketLabel} · {pendingQuote.hlDex || form.hlDex || 'xyz'}
              </dd>
              <dt>Side / action</dt>
              <dd>
                {formatSideLabel(pendingQuote.side, pendingQuote.actionType || form.action)} ·{' '}
                {String(pendingQuote.actionType || form.action).toUpperCase()}
              </dd>
              <dt>Order type</dt>
              <dd>Market (IOC)</dd>
              <dt>Margin</dt>
              <dd>
                {fmtUsd(pendingQuote.deploy)} {pendingQuote.quote || marginQuote}
              </dd>
              <dt>Leverage</dt>
              <dd>{pendingQuote.leverage}x</dd>
              <dt>Notional</dt>
              <dd>≈ {fmtUsd(pendingQuote.notional)}</dd>
              <dt>Est. HL fee</dt>
              <dd>
                {pendingQuote.hlFeeUsd != null && pendingQuote.hlFeeUsd > 0
                  ? fmtUsd(pendingQuote.hlFeeUsd)
                  : '—'}
              </dd>
              <dt>Slippage cap</dt>
              <dd>{formatHlSlippageLabel(pendingQuote.slippageBps)}</dd>
              <dt>Platform commission</dt>
              <dd>{fmtCommissionLine(pendingQuote)}</dd>
              <dt>Size (contracts)</dt>
              <dd>{pendingQuote.size ?? '—'}</dd>
              <dt>Worst price (slippage cap)</dt>
              <dd>{fmtDollar(pendingQuote.limitPx || pendingQuote.refPx)}</dd>
              <dt>HL coin id</dt>
              <dd>
                <code>{pendingQuote.hlCoin}</code>
              </dd>
            </dl>
            {pendingQuote.commissionEnabled && Number(pendingQuote.commissionUsd) > 0 && (
              <p className="manual-quote-note">
                Paper mode: commission ({fmtUsd(pendingQuote.commissionUsd)}) is logged only — no USDC transfer.
              </p>
            )}
            <div className="manual-quote-actions">
              <button type="button" className="secondary-btn" disabled={submitting} onClick={() => setPendingQuote(null)}>
                Cancel
              </button>
              <button type="button" className="primary-btn" disabled={submitting} onClick={confirmPaperQuote}>
                {submitting ? 'Filling…' : 'Confirm paper fill'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
